// File: src/components/layout/FullPageMenu.js

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './FullPageMenu.module.css';
import { NAV_LINKS, SITE_NAME } from '../../config/constants';
import { cn } from '../../utils/helpers';

function FullPageMenu({ isOpen, onClose }) {
  const location = useLocation();
  const [phase, setPhase] = useState('closed');
  // closed → mounted → tiles-in → open → tiles-out → closed
  const timerRef = useRef(null);
  const rafRef = useRef(null);

  const COLS = 10;
  const ROWS = 8;
  const TOTAL = COLS * ROWS;
  const MAX_DIAG = ROWS + COLS - 2;

  // Total time for all tiles to finish animating in
  const TILE_IN_MAX_DELAY = 350;
  const TILE_IN_DURATION = 250;
  const TILES_IN_TOTAL = TILE_IN_MAX_DELAY + TILE_IN_DURATION + 50; // ~650ms

  const TILE_OUT_MAX_DELAY = 500;
  const TILE_OUT_DURATION = 300;
  const TILES_OUT_TOTAL = TILE_OUT_MAX_DELAY + TILE_OUT_DURATION + 50; // ~850ms

  const clearTimers = useCallback(() => {
    clearTimeout(timerRef.current);
    cancelAnimationFrame(rafRef.current);
  }, []);

  // OPENING: isOpen goes true
  useEffect(() => {
    if (isOpen && phase === 'closed') {
      setPhase('mounted');
    }
  }, [isOpen, phase]);

  // Step 2: After mount is painted, trigger tiles-in
  useEffect(() => {
    if (phase === 'mounted') {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setPhase('tiles-in');
        });
      });
    }
  }, [phase]);

  // Step 3: After tiles finish animating in, show content
  useEffect(() => {
    if (phase === 'tiles-in') {
      timerRef.current = setTimeout(() => {
        setPhase('open');
      }, TILES_IN_TOTAL);
    }
  }, [phase, TILES_IN_TOTAL]);

  // CLOSING: isOpen goes false
  useEffect(() => {
    if (!isOpen && (phase === 'open' || phase === 'tiles-in')) {
      clearTimers();
      setPhase('tiles-out');
      timerRef.current = setTimeout(() => {
        setPhase('closed');
      }, TILES_OUT_TOTAL);
    }
  }, [isOpen, phase, clearTimers, TILES_OUT_TOTAL]);

  // Close on route change
  useEffect(() => {
    if (phase === 'open' || phase === 'tiles-in') {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Body scroll lock
  useEffect(() => {
    if (phase !== 'closed') {
      document.body.classList.add('menu-open');
      if (window.__lenis) window.__lenis.stop();
    } else {
      document.body.classList.remove('menu-open');
      if (window.__lenis) window.__lenis.start();
    }
    return () => {
      document.body.classList.remove('menu-open');
      if (window.__lenis) window.__lenis.start();
    };
  }, [phase]);

  // Cleanup
  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  if (phase === 'closed') return null;

  const getTileDelay = (index) => {
    const row = Math.floor(index / COLS);
    const col = index % COLS;
    const diagonal = row + col;

    if (phase === 'tiles-in' || phase === 'open') {
      return (diagonal / MAX_DIAG) * TILE_IN_MAX_DELAY;
    }
    if (phase === 'tiles-out') {
      return ((MAX_DIAG - diagonal) / MAX_DIAG) * TILE_OUT_MAX_DELAY;
    }
    return 0;
  };

  const getTileClass = () => {
    if (phase === 'tiles-in') return styles.tileIn;
    if (phase === 'open') return styles.tileStatic;
    if (phase === 'tiles-out') return styles.tileOut;
    return '';
  };

  const showContent = phase === 'open';
  const tileClass = getTileClass();

  return (
    <div className={styles.wrapper} aria-hidden={phase === 'closed'}>
      {/* Tile grid */}
      <div className={styles.tileGrid}>
        {Array.from({ length: TOTAL }).map((_, i) => (
          <div
            key={i}
            className={cn(styles.tile, tileClass)}
            style={{
              transitionDelay:
                phase === 'tiles-in' || phase === 'tiles-out'
                  ? `${getTileDelay(i)}ms`
                  : '0ms',
            }}
          />
        ))}
      </div>

      {/* Menu content */}
      <div className={cn(styles.content, showContent && styles.contentVisible)}>
        <div className={styles.contentInner}>
          <nav className={styles.nav} aria-label="Main menu">
            <ul className={styles.navList}>
              {NAV_LINKS.filter((l) => l.path !== '/contact').map((link, i) => {
                const isActive = location.pathname === link.path;
                return (
                  <li key={link.path} className={styles.navItem}>
                    <Link
                      to={link.path}
                      className={cn(
                        styles.navLink,
                        isActive && styles.navLinkActive
                      )}
                      style={{
                        transitionDelay: showContent ? `${50 + i * 40}ms` : '0ms',
                      }}
                      onClick={isActive ? onClose : undefined}
                    >
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className={styles.featured}>
            <div className={styles.featuredCard}>
              <div className={styles.featuredImage}>
                <div className={styles.featuredImagePlaceholder}>
                  <img
                    src="/images/menu-stock-image.jpeg"
                    alt="Featured menu item"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.bottomBar}>
          <div className={styles.bottomLeft}>
            <Link to="/privacy-policy" className={styles.bottomLink}>
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className={styles.bottomLink}>
              Terms of Use
            </Link>
          </div>
          <div className={styles.bottomRight}>
            <span className={styles.bottomText}>
              © {new Date().getFullYear()} {SITE_NAME}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FullPageMenu;